import { Injectable } from '@angular/core';
import { AngularFireAnalytics } from '@angular/fire/compat/analytics';
import { environment } from '../../../environments/environment';
import Order from '../../models/Order';
import ArticleGroup from '../../models/ArticleGroup';
import { OrderUtils } from '../../utils/order-utils';
import { PaymentMethod } from '../../enums/PaymentMethod';
import Payment from '../../models/Payment';
import Article from '../../models/Article';
import { OrderType } from '../../enums/OrderType';
import { PreorderType } from '../../enums/PreorderType';
import { getPrice, loadGoogleTag } from '../../utils/utils';

declare const gtag: any;

@Injectable({
	providedIn: 'root',
})
export class AnalyticsService {
	static orderHasProduct = false;

	private currentVenueName = '';
	private currentCountry = '';
	private flowStartTimes: Record<string, number> = {};
	private menuPageVisited = false;
	private checkoutModalOpened = false;
	private paymentModalWasOpened = false;

	constructor(private analytics: AngularFireAnalytics) {}

	startFlow(flowName: string) {
		this.flowStartTimes[flowName] = Date.now();
	}

	endFlow(flowName: string, order: Order, paymentMethod: PaymentMethod | null = null) {
		const startTime = this.flowStartTimes[flowName];
		if (!startTime) {
			return;
		}
		delete this.flowStartTimes[flowName];
		const durationMs = Date.now() - startTime;
		this.logEvent('flow_duration', {
			flow_name: flowName,
			duration_ms: durationMs,
			order_type: order?.type,
			preorder_type: order?.preorder?.type,
			...(paymentMethod ? { payment_method: paymentMethod } : {}),
		});
	}

	setVenue(venueName: string) {
		this.currentVenueName = venueName || '';
	}

	setCountry(country: string) {
		this.currentCountry = country || '';
	}

	venueSelected(venueName: string, orderType: string, preorderType: string) {
		this.menuPageVisited = false;
		this.checkoutModalOpened = false;
		this.paymentModalWasOpened = false;
		this.logEvent('venue_selected', {
			venue_name: venueName,
			order_type: orderType,
			preorder_type: preorderType,
		});
	}

	paymentMethodSelected(
		paymentMethod: PaymentMethod,
		order: Order,
		attemptNumber: number = 1,
		previousMethod: PaymentMethod | null = null
	) {
		const params: any = {
			payment_method: paymentMethod,
			order_type: order?.type,
			preorder_type: order?.preorder?.type,
			attempt_number: attemptNumber,
		};
		if (previousMethod) {
			params.previous_method = previousMethod;
		}
		this.logEvent('payment_method_selected', params);
	}

	logVersion() {
		this.logEvent('version', {
			customer: environment.customerGroup,
			version: environment.version,
		});
	}

	addToCart(order: Order, articleGroup: ArticleGroup) {
		this.logEvent('add_to_cart', {
			currency: order.currency,
			value: OrderUtils.totalPrice(articleGroup, order.type, order.preorder?.type, order.terminalorder?.type),
			order_type: order.type,
			preorder_type: order.preorder?.type,
			items: [
				{
					id: articleGroup.article.masterId,
					name: articleGroup.article.name.de,
					quantity: articleGroup.quantity,
					price: OrderUtils.totalPrice(articleGroup, order.type, order.preorder?.type, order.terminalorder?.type),
				},
			],
		});
	}

	beginCheckout(order: Order) {
		this.logEvent('begin_checkout', {
			currency: order.currency,
			value: OrderUtils.orderTotalPrice(order, true, true),
			items: order.orderedArticles.map(orderedArticle => {
				return {
					id: orderedArticle.article.masterId,
					name: orderedArticle.article.name.de,
					quantity: orderedArticle.quantity,
					price: OrderUtils.totalPrice(orderedArticle, order.type, order.preorder?.type, order.terminalorder?.type),
				};
			}),
			coupon: order.promoCode && order.promoCode.code ? order.promoCode.code : undefined,
		});
	}

	emailVerificationPageOpened() {
		this.logEvent('email_verification_page_opened');
	}

	orderVerified(order: Order) {
		this.logEvent('order_verified', {
			currency: order.currency,
			value: OrderUtils.orderTotalPrice(order, true, true),
			items: order.orderedArticles.map(orderedArticle => {
				return {
					id: orderedArticle.article.masterId,
					name: orderedArticle.article.name.de,
					quantity: orderedArticle.quantity,
					price: OrderUtils.totalPrice(orderedArticle, order.type, order.preorder?.type, order.terminalorder?.type),
				};
			}),
			coupon: order.promoCode && order.promoCode.code ? order.promoCode.code : undefined,
		});
		this.checkoutProgress(order, null, 2);
	}

	checkoutProgress(order: Order, paymentMethod: PaymentMethod, checkoutStep: number) {
		this.logEvent('checkout_progress', {
			currency: order.currency,
			value: OrderUtils.orderTotalPrice(order, true, true),
			items: order.orderedArticles.map(orderedArticle => {
				return {
					id: orderedArticle.article.masterId,
					name: orderedArticle.article.name.de,
					quantity: orderedArticle.quantity,
					price: OrderUtils.totalPrice(orderedArticle, order.type, order.preorder?.type, order.terminalorder?.type),
				};
			}),
			checkout_option: paymentMethod,
			coupon: order.promoCode && order.promoCode.code ? order.promoCode.code : undefined,
		});
	}

	paymentAbandoned(order: Order, paymentMethod: PaymentMethod) {
		this.logEvent('payment_abandoned', {
			payment_method: paymentMethod,
			order_type: order?.type,
			preorder_type: order?.preorder?.type,
		});
	}

	paymentLoadingFailed(
		order: Order,
		paymentMethod: PaymentMethod,
		errorType: string = 'payment_load_failed',
		extraParams: Record<string, any> = {}
	) {
		this.logEvent('payment_loading_failed', {
			currency: order.currency,
			value: OrderUtils.orderTotalPrice(order, true, true),
			order_type: order.type,
			preorder_type: order.preorder?.type,
			payment_method: paymentMethod,
			error_type: errorType,
			coupon: order.promoCode?.code ?? undefined,
			...extraParams,
		});
	}

	paymentFailed(order: Order, payment: Payment, paymentMethod: PaymentMethod, errorType: string = 'unknown', extraParams: Record<string, any> = {}) {
		this.logEvent('payment_failed', {
			order: order._id,
			payment: payment._id,
			payment_method: paymentMethod,
			order_type: order?.type,
			preorder_type: order?.preorder?.type,
			error_type: errorType,
			...extraParams,
		});
	}

	paymentLoaded(order: Order, paymentMethod: PaymentMethod) {
		this.logEvent('payment_loaded', {
			currency: order.currency,
			value: OrderUtils.orderTotalPrice(order, true, true),
			order_type: order.type,
			preorder_type: order.preorder?.type,
			payment_method: paymentMethod,
			coupon: order.promoCode && order.promoCode.code ? order.promoCode.code : undefined,
		});
		this.checkoutProgress(order, paymentMethod, 3);
	}

	purchase(order: Order, payment: Payment) {
		this.logEvent('purchase', {
			currency: order.currency,
			value: OrderUtils.orderTotalPrice(order, true, true),
			payment_method: payment?.method,
			order_type: order?.type,
			preorder_type: order?.preorder?.type,
			items: order.orderedArticles.map(orderedArticle => {
				return {
					id: orderedArticle.article.masterId,
					name: orderedArticle.article.name.de,
					quantity: orderedArticle.quantity,
					price: OrderUtils.totalPrice(orderedArticle, order.type, order.preorder?.type, order.terminalorder?.type),
				};
			}),
			transaction_id: payment._id,
			shipping: order.preorder?.deliveryFee ?? undefined,
			coupon: order.promoCode?.code ?? undefined,
		});
	}

	search(searchTerm: string) {
		this.logEvent('search', {
			search_term: searchTerm,
		});
	}

	selectContentProduct(article: Article, orderType: OrderType, preorderType: PreorderType) {
		this.logEvent('select_content', {
			content_type: 'product',
			items: {
				id: article.masterId,
				name: article.name.de,
				quantity: 1,
				price: getPrice(article, orderType, preorderType, null),
			},
		});
	}

	visitMenuPage(order: Order) {
		if (this.menuPageVisited) {
			return;
		}
		this.menuPageVisited = true;
		this.logEvent('visit_menu_page', {
			order_type: order?.type,
			preorder_type: order?.preorder?.type,
		});
	}

	openOrderContent() {
		this.logEvent('visit_order_content');
	}

	orderHasProducts() {
		if (AnalyticsService.orderHasProduct) {
			return;
		}
		AnalyticsService.orderHasProduct = true;
		this.logEvent('order_has_products');
	}

	customerBeginsCheckout(order: Order) {
		if (this.checkoutModalOpened) {
			return;
		}
		this.checkoutModalOpened = true;
		this.logEvent('checkout_modal_opened', {
			order_type: order?.type,
			preorder_type: order?.preorder?.type,
		});
	}

	paymentModalOpened(order: Order) {
		if (this.paymentModalWasOpened) {
			return;
		}
		this.paymentModalWasOpened = true;
		this.logEvent('payment_modal_opened', {
			order_type: order?.type,
			preorder_type: order?.preorder?.type,
		});
	}

	paymentSuccess(paymentMethod: PaymentMethod, transactionId: string, order: Order) {
		this.logEvent('payment_success', {
			payment_method: paymentMethod,
			transaction_id: transactionId,
			order_type: order?.type,
			preorder_type: order?.preorder?.type,
		});
	}

	screenLoadTime(screenName: string, loadTimeMs: number) {
		this.logEvent('screen_load_time', {
			screen_name: screenName,
			load_time_ms: loadTimeMs,
		});
	}

	orderCreationAttempted(order: Order) {
		this.logEvent('order_creation_attempted', {
			currency: order.currency,
			value: OrderUtils.orderTotalPrice(order, true, true),
			order_type: order.type,
			preorder_type: order.preorder?.type,
		});
	}

	orderCreationFailed(order: Order, errorType: string, extraParams: Record<string, any> = {}) {
		this.logEvent('order_creation_failed', {
			currency: order.currency,
			value: OrderUtils.orderTotalPrice(order, true, true),
			order_type: order.type,
			preorder_type: order.preorder?.type,
			error_type: errorType,
			...extraParams,
		});
	}

	viewItem(article: ArticleGroup, order: Order) {
		const eventParams = {
			currency: order.currency,
			value: OrderUtils.totalPrice(article, order.type, order.preorder?.type, order.terminalorder?.type),
			items: [
				{
					id: article.article.masterId,
					name: article.article.name.de,
					quantity: article.quantity,
					price: OrderUtils.totalPrice(article, order.type, order.preorder?.type, order.terminalorder?.type),
				},
			],
		};
		this.logEvent('view_item', eventParams);
	}

	beginArticleEdit(article: Article) {
		const eventParams = {
			article: article.name.de,
			id: article._id,
		};
		this.logEvent('begin_article_edit', eventParams);
	}

	stepArticleEdit(article: Article, step: number) {
		const eventParams = {
			article: article.name.de,
			step,
			id: article._id,
		};
		this.logEvent('step_article_edit', eventParams);
	}

	finishArticleEdit(article: Article, step: number) {
		const eventParams = {
			article: article.name.de,
			step,
			id: article._id,
		};
		this.logEvent('finish_article_edit', eventParams);
	}

	cancelArticleEdit(article: Article, step: number) {
		this.logEvent('cancel_article_edit', {
			article: article.name.de,
			step,
			id: article._id,
		});
	}

	async setAnalyticsCollectionEnabled(enabled: boolean) {
		console.log(`setAnalyticsCollectionEnabled(enabled: ${enabled})`);
		await this.analytics.setAnalyticsCollectionEnabled(enabled);
		if (environment.gaId) {
			if (enabled) {
				window['ga-disable-' + environment.gaId] = false;
				loadGoogleTag(environment.gaId);
			} else {
				window['ga-disable-' + environment.gaId] = true;
			}
		}
	}

	loadFacebookPixel(id: string) {
		const nodeId = 'facebook-pixel';
		if (id) {
			const script = document.getElementById(nodeId) || document.createElement('script');
			script.id = nodeId;
			script.innerHTML =
				'!function(f,b,e,v,n,t,s)\n' +
				'{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\n' +
				'n.callMethod.apply(n,arguments):n.queue.push(arguments)};\n' +
				"if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';\n" +
				'n.queue=[];t=b.createElement(e);t.async=!0;\n' +
				't.src=v;s=b.getElementsByTagName(e)[0];\n' +
				"s.parentNode.insertBefore(t,s)}(window, document,'script',\n" +
				"'https://connect.facebook.net/en_US/fbevents.js');\n" +
				"fbq('init', '" +
				id +
				"');\n" +
				"fbq('track', 'PageView');";
			if (!script.parentNode) {
				document.body.appendChild(script);
			}
		} else {
			const script = document.getElementById(nodeId);
			if (script) {
				document.removeChild(script);
			}
		}
	}

	private logEvent(eventName: string, eventParams: any = {}) {
		const context: any = {};
		if (this.currentVenueName) {
			context.venue_name = this.currentVenueName;
		}
		if (this.currentCountry) {
			context.country = this.currentCountry;
		}
		const params = { ...context, ...eventParams };
		if (typeof gtag !== 'undefined' && environment.gaId) {
			gtag('event', eventName, params);
		}
		this.analytics.logEvent(eventName, params).then(() => {
			if (environment.production) {
				return;
			}
		});
	}
}
